import { useState, useEffect } from 'react';
import { useCashSession } from '../../hooks/useCashSession';
import cashRegisterService from '../../services/cashRegisterService';
import CashSessionOpen from '../../components/cash/CashSessionOpen';
import CashSessionActive from '../../components/cash/CashSessionActive';
import CashSessionClose from '../../components/cash/CashSessionClose';

const CashRegisterPage = () => {
  const [cashRegisters, setCashRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [loadingRegisters, setLoadingRegisters] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const {
    activeSession,
    loading: sessionLoading,
    hasActiveSession,
    openSession,
    closeSession,
    refresh,
  } = useCashSession();

  // Load cash registers on mount
  useEffect(() => {
    loadCashRegisters();
  }, []);

  const loadCashRegisters = async () => {
    try {
      setLoadingRegisters(true);
      const response = await cashRegisterService.getAll();

      if (response.success && response.data) {
        setCashRegisters(response.data);

        // If there's an active session, select that register
        if (activeSession) {
          const activeCashRegister = response.data.find(
            (cr) => cr.id === activeSession.cash_register_id
          );
          setSelectedRegister(activeCashRegister);
        } else if (response.data.length > 0) {
          // If no active session, select the first register
          setSelectedRegister(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Error loading cash registers:', error);
    } finally {
      setLoadingRegisters(false);
    }
  };

  const handleOpenSession = async (data) => {
    const result = await openSession({
      ...data,
      cash_register_id: selectedRegister.id,
    });

    if (result.success) {
      setShowOpenModal(false);
      refresh();
    }

    return result;
  };

  const handleCloseSession = async (data) => {
    const result = await closeSession(activeSession.id, data);

    if (result.success) {
      setShowCloseModal(false);
      refresh();
    }

    return result;
  };

  if (loadingRegisters || sessionLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (cashRegisters.length === 0) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <h5>No cash registers configured</h5>
          <p>Please contact your administrator to set up a cash register.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-3">
            <i className="bi bi-cash-register me-2"></i>
            Cash Register Control
          </h2>
        </div>
      </div>

      {/* Register Selector */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <label className="form-label fw-bold">Cash Register</label>
              <select
                className="form-select"
                value={selectedRegister?.id || ''}
                onChange={(e) => {
                  const register = cashRegisters.find(
                    (cr) => cr.id === parseInt(e.target.value)
                  );
                  setSelectedRegister(register);
                }}
                disabled={hasActiveSession}
              >
                {cashRegisters.map((register) => (
                  <option key={register.id} value={register.id}>
                    {register.nombre} - {register.punto_venta.nombre}
                    {register.tiene_sesion_abierta && ' (Open)'}
                  </option>
                ))}
              </select>
              {hasActiveSession && (
                <small className="text-muted">
                  You can't switch registers while a session is open
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="fw-bold">Status: </span>
                  {hasActiveSession ? (
                    <span className="badge bg-success">Session Open</span>
                  ) : (
                    <span className="badge bg-secondary">No Session</span>
                  )}
                </div>

                <div>
                  {!hasActiveSession ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowOpenModal(true)}
                      disabled={!selectedRegister}
                    >
                      <i className="bi bi-door-open me-2"></i>
                      Open Register
                    </button>
                  ) : (
                    <button className="btn btn-danger" onClick={() => setShowCloseModal(true)}>
                      <i className="bi bi-door-closed me-2"></i>
                      Close Register
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Session */}
      {hasActiveSession && (
        <div className="row">
          <div className="col-12">
            <CashSessionActive session={activeSession} onRefresh={refresh} />
          </div>
        </div>
      )}

      {/* No Session */}
      {!hasActiveSession && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              There's no open cash session. Click "Open Register" to get started.
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showOpenModal && (
        <CashSessionOpen
          show={showOpenModal}
          onClose={() => setShowOpenModal(false)}
          onSubmit={handleOpenSession}
          cashRegister={selectedRegister}
        />
      )}

      {showCloseModal && activeSession && (
        <CashSessionClose
          show={showCloseModal}
          onClose={() => setShowCloseModal(false)}
          onSubmit={handleCloseSession}
          session={activeSession}
        />
      )}
    </div>
  );
};

export default CashRegisterPage;